import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Shield, Settings, Hammer, Car, Package, ArrowLeft, Clock, PenTool as Tool, DollarSign, CheckCircle } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';

// Service details with additional information
const serviceDetails: Record<string, ServiceDetail> = {
  'PPF (Paint Protection Film)': {
    description: 'Premium paint protection film that shields your vehicle\'s paint from scratches, chips, and environmental damage.',
    benefits: [
      'Invisible protection for your car\'s paint',
      'Self-healing technology',
      'UV protection to prevent yellowing',
      'Up to 10 years warranty',
      'Maintains resale value'
    ],
    process: [
      'Surface preparation and cleaning',
      'Paint correction if needed',
      'Custom cut film application',
      'Heat treatment and edge sealing',
      'Quality inspection'
    ],
    duration: '1-3 days',
    warranty: '5-10 years',
    maintenance: 'Low maintenance, regular washing only'
  },
  'Ceramic Coating': {
    description: 'Advanced liquid polymer that chemically bonds with your vehicle\'s paint, creating a permanent protective layer.',
    benefits: [
      'Superior gloss and shine',
      'Hydrophobic properties',
      'Chemical resistance',
      'UV protection',
      'Easier cleaning and maintenance'
    ],
    process: [
      'Deep cleaning and decontamination',
      'Paint correction',
      'Coating application',
      'Curing time',
      'Final inspection'
    ],
    duration: '3-5 days',
    warranty: '2-5 years',
    maintenance: 'Regular washing with pH neutral soap'
  }
};

export const serviceCategories = {
  'car-care': {
    icon: Shield,
    key: 'carCare',
    image: 'https://images.unsplash.com/photo-1617531653332-bd46c24f2068?auto=format&fit=crop&q=80',
    services: [
      'PPF (Paint Protection Film)',
      'Ceramic Coating',
      'Window Tinting',
      'Detailing',
      'Color Wrapping',
      'Headlight Restoration',
      'Interior & Exterior Restoration'
    ]
  },
  'body-works': {
    icon: Hammer,
    key: 'bodyWorks',
    image: 'https://i.postimg.cc/h4NB66D4/bodywork.jpg',
    services: [
      'Painting & Custom Paint Jobs',
      'Rim Repairing & Customization',
      'Dent Repairing',
      'Scratch Repairing & Buffing',
      'Car Body Parts Adjustments',
      'Rust Removal & Prevention'
    ]
  },
  'mechanical': {
    icon: Settings,
    key: 'mechanical',
    image: 'https://i.postimg.cc/BbzQVnhJ/MWT2syjf4-NVi-R6l-Yy4q-WJRt-WCx-C3-T8-K7-Giq6w-Gan.jpg',
    services: [
      'Transmission & Engine Overhaul',
      'Part Swaps & Installation',
      'Tuning & Performance Upgrades',
      'Suspension Repairs & Upgrades',
      'Brake System Repairs',
      'Cooling System Repairs',
      'Electrical Repairs'
    ]
  },
  'service': {
    icon: Car,
    key: 'service',
    image: 'https://i.postimg.cc/ZKyTqZ7y/FAh6ue9-WMTu-DT3-VNSf5-Ev7-VNs61cuez1-HDKBIJon.jpg',
    services: [
      'Oil Change',
      'Tyre Change & Rotation',
      'Brake Pads Replacement',
      'Diagnostic Inspection',
      'Filter Replacement',
      'Fluids Top-Up',
      'AC Service & Gas Refilling'
    ]
  },
  'parts': {
    icon: Package,
    key: 'parts',
    image: 'https://i.postimg.cc/HxMSycbM/istockphoto-478107962-612x612-1.jpg',
    services: [
      'Engine Components',
      'Suspension Parts',
      'Electrical Components',
      'Body Panels & Trims',
      'Wheels & Tires',
      'Interior Accessories'
    ]
  }
};

export function ServicesPage() {
  const { category, service } = useParams();
  const { t } = useTranslation();
  const serviceInfo = category ? serviceCategories[category as keyof typeof serviceCategories] : null;

  // If a specific service is selected
  if (category && service && serviceInfo) {
    const decodedService = decodeURIComponent(service);
    const serviceDetail = serviceDetails[decodedService];
    const categoryInfo = t.services.categories[serviceInfo.key];

    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-800">
        {/* Hero Section */}
        <div className="relative bg-cover bg-center h-[300px]"
          style={{
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url("${serviceInfo.image}")`
          }}>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center text-white px-4">
              <Link 
                to={`/services/${category}`}
                className="flex items-center justify-center mb-4 text-white hover:text-blue-300 transition-colors"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                {t.common.backTo} {categoryInfo.title}
              </Link>
              <h1 className="text-4xl font-bold mb-4">{decodedService}</h1>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {serviceDetail ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Main Content */}
              <div className="lg:col-span-2 space-y-8">
                <div className="bg-white dark:bg-gray-700 rounded-lg shadow-lg p-6">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                    {t.common.overview}
                  </h2>
                  <p className="text-gray-600 dark:text-gray-300">
                    {serviceDetail.description}
                  </p>
                </div>

                <div className="bg-white dark:bg-gray-700 rounded-lg shadow-lg p-6">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                    {t.common.benefits}
                  </h2>
                  <ul className="space-y-3">
                    {serviceDetail.benefits.map((benefit, index) => (
                      <li key={index} className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-600 dark:text-gray-300">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="bg-white dark:bg-gray-700 rounded-lg shadow-lg p-6">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                    {t.common.process}
                  </h2>
                  <div className="space-y-4">
                    {serviceDetail.process.map((step, index) => (
                      <div key={index} className="flex items-start">
                        <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center mr-3">
                          {index + 1}
                        </div>
                        <div className="text-gray-600 dark:text-gray-300 pt-1">
                          {step}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                <div className="bg-white dark:bg-gray-700 rounded-lg shadow-lg p-6">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                    {t.common.serviceDetails}
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-3" />
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{t.common.duration}</p>
                        <p className="text-gray-900 dark:text-white">{serviceDetail.duration}</p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <Tool className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-3" />
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{t.common.warranty}</p>
                        <p className="text-gray-900 dark:text-white">{serviceDetail.warranty}</p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <DollarSign className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-3" />
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{t.common.maintenance}</p>
                        <p className="text-gray-900 dark:text-white">{serviceDetail.maintenance}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white dark:bg-gray-700 rounded-lg shadow-lg p-6">
                  <button
                    className="w-full bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors"
                    onClick={() => {/* TODO: Implement booking logic */}}
                  >
                    {t.common.bookNow}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-600 dark:text-gray-300">
                Service details not available
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Category view
  if (serviceInfo) {
    const categoryInfo = t.services.categories[serviceInfo.key];

    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-800">
        {/* Hero Section */}
        <div className="relative bg-cover bg-center h-[300px]"
          style={{
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url("${serviceInfo.image}")`
          }}>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center text-white px-4">
              <Link 
                to="/services"
                className="flex items-center justify-center mb-4 text-white hover:text-blue-300 transition-colors"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                {t.common.backTo} {t.common.allServices}
              </Link>
              <div className="flex items-center justify-center mb-4">
                <serviceInfo.icon className="w-12 h-12 mr-4" />
                <h1 className="text-4xl font-bold">{categoryInfo.title}</h1>
              </div>
              <p className="text-xl max-w-2xl">
                {categoryInfo.description}
              </p>
            </div>
          </div>
        </div>

        {/* Services List */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {categoryInfo.services.map((service, index) => (
              <Link
                key={index}
                to={`/services/${category}/${encodeURIComponent(service)}`}
                className="bg-white dark:bg-gray-700 rounded-lg shadow-lg p-6 transition-all hover:shadow-xl hover:scale-105"
              >
                <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                  {service}
                </h3>
                <div className="flex justify-between items-center">
                  <span className="text-blue-600 dark:text-blue-400">{t.common.learnMore}</span>
                  <ArrowLeft className="w-5 h-5 transform rotate-180" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Main services page
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-800">
      {/* Hero Section */}
      <div className="relative bg-cover bg-center h-[400px]"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url("https://i.postimg.cc/8PYZMkyb/659942e355ab821f56f9efc3-porsche-911.png")`
        }}>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white px-4">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              {t.services.title}
            </h1>
            <p className="text-xl max-w-2xl">
              {t.services.subtitle}
            </p>
          </div>
        </div>
      </div>

      {/* Categories Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {Object.entries(serviceCategories).map(([key, category]) => {
            const categoryInfo = t.services.categories[category.key];
            return (
              <Link
                key={key}
                to={`/services/${key}`}
                className="bg-white dark:bg-gray-700 rounded-lg shadow-lg overflow-hidden transition-transform hover:scale-105"
              >
                <div 
                  className="h-48 bg-cover bg-center"
                  style={{ backgroundImage: `url(${category.image})` }}
                />
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    <category.icon className="w-8 h-8 text-blue-600 dark:text-blue-400 mr-3" />
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                      {categoryInfo.title}
                    </h2>
                  </div>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">
                    {categoryInfo.description}
                  </p>
                  <div className="space-y-2">
                    {categoryInfo.services.map((service, index) => (
                      <div 
                        key={index}
                        className="flex items-center text-gray-600 dark:text-gray-300"
                      >
                        <div className="w-1.5 h-1.5 rounded-full bg-blue-600 dark:bg-blue-400 mr-2" />
                        {service}
                      </div>
                    ))}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}